// Copyright ©️ 2025 eVolve MEP, LLC

import React, { type Ref, useCallback, useEffect, useRef, useState } from 'react';

import { Textarea, type TextareaProps, TextInput, type TextInputProps } from '@mantine/core';

import { isNotNil } from 'helpers/isNotNil';
import { omit } from 'helpers/omit';
import { strongKeys } from 'helpers/strongEntries';

type Props<T extends HTMLInputElement | HTMLTextAreaElement> = {
  delay?: number;
  defaultValue?: string;
  /** Debounced */
  onChange: (debouncedValue: string) => void;
  /**
   * If `true`, will call `onChange` on an immediate change before waiting for a debounce to occur.
   * @default false
   * */
  leading?: boolean;
  value?: string;
  ref?: Ref<T>;
};

const useDebouncedValue = <T extends HTMLInputElement | HTMLTextAreaElement>({
  value: externalValue,
  onChange: externalOnChange,
  defaultValue = '',
  leading = false,
  delay = 500,
}: Props<T>) => {
  const valueHasEverChangedRef = useRef(false);
  const [value, setValue] = useState(defaultValue);
  const timeoutId = useRef<ReturnType<typeof setTimeout>>(null);
  useEffect(() => {
    if (isNotNil(externalValue)) {
      setValue(externalValue);
      valueHasEverChangedRef.current = false;
    }
  }, [externalValue]);

  useEffect(() => {
    if (!valueHasEverChangedRef.current) return undefined;
    if (!timeoutId.current && leading) externalOnChange(value);
    timeoutId.current = setTimeout(() => {
      externalOnChange(value);
    }, delay);
    return () => {
      if (timeoutId.current) clearTimeout(timeoutId.current);
      timeoutId.current = null;
    };
  }, [value, delay, externalOnChange, leading]);

  const onChange = useCallback((e: React.ChangeEvent<T>) => {
    valueHasEverChangedRef.current = true;
    setValue(e.currentTarget.value);
  }, []);

  return { value, onChange };
};

export const TextInputDebounced = (
  props: Props<HTMLInputElement> & Omit<TextInputProps, keyof Props<HTMLInputElement>>,
) => {
  const { leading: _, ...rest } = props;
  const debounceProps = useDebouncedValue(props);
  return <TextInput {...omit(rest, strongKeys(debounceProps))} {...debounceProps} />;
};

export const TextareaDebounced = (
  props: Props<HTMLTextAreaElement> & Omit<TextareaProps, keyof Props<HTMLTextAreaElement>>,
) => {
  const { leading: _, ...rest } = props;
  const debounceProps = useDebouncedValue(props);
  return <Textarea {...omit(rest, strongKeys(debounceProps))} {...debounceProps} />;
};
